import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import Drawer from '../../commons/drawer/Drawer';
import {
    ClinicalRecordMeta, clinicalRecordByAppointment, clinicalRecordContent,
    saveClinicalRecord, signClinicalRecord,
} from '../../../service/clinical/clinical-service';

const SECTIONS = ['queixa', 'historico', 'avaliacao', 'plano'] as const;
type Sections = Record<typeof SECTIONS[number], string>;
const empty = (): Sections => ({ queixa: '', historico: '', avaliacao: '', plano: '' });

interface Props {
    businessId: number;
    appointmentId: string;
    onClose: () => void;
}

/**
 * Prontuário editor opened FROM the appointment — the only place a record is
 * created (backend enforces: attending professional only, one per
 * appointment, client taken from the appointment itself).
 */
const ClinicalRecordModal: React.FC<Props> = ({ businessId, appointmentId, onClose }) => {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const [meta, setMeta] = useState<ClinicalRecordMeta | null>(null);
    const [sections, setSections] = useState<Sections>(empty());
    const [locked, setLocked] = useState<string | null>(null);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const existing = await clinicalRecordByAppointment(businessId, appointmentId, token);
                if (existing) {
                    setMeta(existing);
                    try {
                        const res = await clinicalRecordContent(businessId, existing.id, token);
                        let parsed = empty();
                        try { parsed = { ...parsed, ...JSON.parse(res.content) }; } catch { parsed.historico = res.content; }
                        setSections(parsed);
                    } catch (e) {
                        setLocked(e instanceof Error ? e.message : t('clinical.locked'));
                    }
                }
            } catch (e) {
                setError(e instanceof Error ? e.message : 'error');
            } finally { setBusy(false); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, appointmentId]);

    const save = async (sign: boolean) => {
        setBusy(true); setError(null);
        try {
            const token = await getAccessTokenSilently();
            const saved = await saveClinicalRecord(businessId, {
                id: meta?.id ?? null, appointmentId, content: JSON.stringify(sections),
            }, token);
            if (sign) await signClinicalRecord(businessId, saved.id, token);
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'error');
        } finally { setBusy(false); }
    };

    const signed = meta?.status === 'SIGNED';
    return (
        <Drawer open onClose={onClose} title={t('clinical.modalTitle')} size="lg"
            footer={!locked && !signed ? (
                <>
                    <button type="button" className="btn btn-outline-primary" disabled={busy}
                            onClick={() => void save(false)} data-cy="clinical-modal-draft">
                        {t('clinical.saveDraft')}
                    </button>
                    <button type="button" className="btn btn-primary" disabled={busy}
                            onClick={() => void save(true)} data-cy="clinical-modal-sign">
                        ✍️ {t('clinical.sign')}
                    </button>
                    <button type="button" className="btn btn-outline-secondary ms-auto" onClick={onClose}>
                        {t('common.cancel')}
                    </button>
                </>
            ) : (
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                    {t('clinical.back')}
                </button>
            )}>
            {busy && !error ? <div className="text-muted p-2">…</div> : locked ? (
                <div className="alert alert-secondary" data-cy="clinical-modal-locked">🔒 {locked}</div>
            ) : (
                <form className="event-form" onSubmit={(e) => e.preventDefault()} data-cy="clinical-modal">
                    {error && <div className="alert alert-danger py-2 px-3">{error}</div>}
                    {signed && <div className="alert alert-success py-1 px-2">✍️ {t('clinical.signedNote')}</div>}
                    {SECTIONS.map((k) => (
                        <div className="form-group" key={k}>
                            <label>{t(`clinical.section.${k}`)}</label>
                            <textarea className="form-control" rows={3} value={sections[k]}
                                disabled={signed || busy}
                                onChange={(e) => setSections((p) => ({ ...p, [k]: e.target.value }))} />
                        </div>
                    ))}
                </form>
            )}
        </Drawer>
    );
};

export default ClinicalRecordModal;
